// src/services/analytics.service.js
import { api } from './api';
import attendanceService from './attendance.service';
import leaveService from './leave.service';
import employeeService from './employee.service';

class AnalyticsService {
    // ============================================================
    // ATTENDANCE ANALYTICS
    // ============================================================

    /**
     * Get attendance analytics for a specific period
     */
    async getAttendanceAnalytics(startDate, endDate) {
        try {
            const response = await api.get('/api/analytics/attendance', {
                params: { startDate, endDate }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching attendance analytics:', error);
            throw error;
        }
    }

    /**
     * Get attendance trends over time
     */
    async getAttendanceTrends(months = 6) {
        try {
            const response = await api.get('/api/analytics/attendance/trends', {
                params: { months }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching attendance trends:', error);
            throw error;
        }
    }

    /**
     * Get department-wise attendance statistics
     */
    async getDepartmentAttendance(startDate, endDate) {
        try {
            const response = await api.get('/api/analytics/attendance/department', {
                params: { startDate, endDate }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching department attendance:', error);
            throw error;
        }
    }

    // ============================================================
    // LEAVE ANALYTICS
    // ============================================================

    /**
     * Get leave analytics for a specific period
     */
    async getLeaveAnalytics(startDate, endDate) {
        try {
            const response = await api.get('/api/analytics/leaves', {
                params: { startDate, endDate }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching leave analytics:', error);
            throw error;
        }
    }

    /**
     * Get leave trends over time
     */
    async getLeaveTrends(months = 6) {
        try {
            const response = await api.get('/api/analytics/leaves/trends', {
                params: { months }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching leave trends:', error);
            throw error;
        }
    }

    /**
     * Get leave type distribution
     */
    async getLeaveTypeDistribution(month, year) {
        try {
            const response = await api.get('/api/analytics/leaves/distribution', {
                params: { month, year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching leave distribution:', error);
            throw error;
        }
    }

    // ============================================================
    // EMPLOYEE ANALYTICS
    // ============================================================

    /**
     * Get employee demographics and statistics
     */
    async getEmployeeAnalytics() {
        try {
            const response = await api.get('/api/analytics/employees');
            return response.data;
        } catch (error) {
            console.error('Error fetching employee analytics:', error);
            throw error;
        }
    }

    /**
     * Get headcount trends over time
     */
    async getHeadcountTrends(months = 12) {
        try {
            const response = await api.get('/api/analytics/employees/headcount', {
                params: { months }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching headcount trends:', error);
            throw error;
        }
    }

    /**
     * Get turnover analytics
     */
    async getTurnoverAnalytics(year) {
        try {
            const response = await api.get('/api/analytics/employees/turnover', {
                params: { year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching turnover analytics:', error);
            throw error;
        }
    }

    // ============================================================
    // PAYROLL/FINANCE ANALYTICS
    // ============================================================

    /**
     * Get payroll analytics for a specific period
     */
    async getPayrollAnalytics(startDate, endDate, department) {
        try {
            const params = { startDate, endDate };
            if (department) params.department = department;
            const response = await api.get('/api/analytics/payroll', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching payroll analytics:', error);
            throw error;
        }
    }

    /**
     * Get department-wise cost analysis
     */
    async getDepartmentCostAnalysis(month, year) {
        try {
            const response = await api.get('/api/analytics/payroll/department-costs', {
                params: { month, year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching department costs:', error);
            throw error;
        }
    }

    /**
     * Get payroll trends over time
     */
    async getPayrollTrends(months = 12) {
        try {
            const response = await api.get('/api/analytics/payroll/trends', {
                params: { months }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching payroll trends:', error);
            throw error;
        }
    }

    // ============================================================
    // PERFORMANCE ANALYTICS
    // ============================================================

    /**
     * Get overall HR metrics dashboard
     */
    async getHRMetricsDashboard(startDate, endDate) {
        try {
            const params = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const response = await api.get('/api/analytics/dashboard', { params });
            return response;
        } catch (error) {
            console.error('Error fetching HR metrics:', error);
            throw error;
        }
    }

    /**
     * Get custom analytics report
     */
    async getCustomReport(reportType, params) {
        try {
            const response = await api.get(`/api/analytics/reports/${reportType}`, {
                params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching custom report:', error);
            throw error;
        }
    }

    // ============================================================
    // NEW ANALYTICS ENDPOINTS
    // ============================================================

    /**
     * Get period comparison analytics
     */
    async getComparisonAnalytics(currentStart, currentEnd, previousStart, previousEnd) {
        try {
            const response = await api.get('/api/analytics/compare', {
                params: { currentStart, currentEnd, previousStart, previousEnd }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching comparison analytics:', error);
            throw error;
        }
    }

    /**
     * Get daily attendance details for a specific date
     */
    async getDailyAttendance(date) {
        try {
            const response = await api.get('/api/analytics/attendance/daily', {
                params: { date }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching daily attendance:', error);
            throw error;
        }
    }

    /**
     * Get attendance heatmap data for a month
     */
    async getAttendanceHeatmap(month) {
        try {
            const response = await api.get('/api/analytics/attendance/heatmap', {
                params: { month }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching attendance heatmap:', error);
            throw error;
        }
    }

    /**
     * Get leave balance overview for all employees
     */
    async getLeaveBalance() {
        try {
            const response = await api.get('/api/analytics/leaves/balance');
            return response.data;
        } catch (error) {
            console.error('Error fetching leave balance:', error);
            throw error;
        }
    }

    /**
     * Get employee demographics
     */
    async getEmployeeDemographics() {
        try {
            const response = await api.get('/api/analytics/employees/demographics');
            return response.data;
        } catch (error) {
            console.error('Error fetching employee demographics:', error);
            throw error;
        }
    }

    /**
     * Get attrition analytics
     */
    async getAttritionAnalytics(year) {
        try {
            const response = await api.get('/api/analytics/employees/attrition', {
                params: { year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching attrition analytics:', error);
            throw error;
        }
    }

    /**
     * Get department-wise payroll costs
     */
    async getDepartmentPayroll(startDate, endDate) {
        try {
            const response = await api.get('/api/analytics/payroll/department', {
                params: { startDate, endDate }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching department payroll:', error);
            throw error;
        }
    }

    /**
     * Get salary distribution across ranges
     */
    async getSalaryDistribution() {
        try {
            const response = await api.get('/api/analytics/payroll/distribution');
            return response.data;
        } catch (error) {
            console.error('Error fetching salary distribution:', error);
            throw error;
        }
    }

    /**
     * Get filtered analytics based on multiple criteria
     */
    async getFilteredAnalytics(filters) {
        try {
            const response = await api.get('/api/analytics/filtered', {
                params: filters
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching filtered analytics:', error);
            throw error;
        }
    }

    // ============================================================
    // EXPORT FUNCTIONALITY
    // ============================================================

    /**
     * Export analytics data to Excel
     */
    async exportAnalytics(type, params = {}) {
        try {
            const response = await api.get(`/api/analytics/export/${type}`, {
                params,
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${type}_analytics_${Date.now()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error exporting analytics:', error);
            throw error;
        }
    }
}

export default new AnalyticsService();
